import React, { useState } from "react";
import { CheckCircle } from "lucide-react";
import { CARD } from "@/styles/shared";

const DASH_CARD = {
  ...CARD,
  background: "rgba(255,255,255,0.82)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(255,255,255,0.7)",
};

export default function QuickWinsSection({ tasks, inboxTotal, onComplete }) {
  const [completingIds, setCompletingIds] = useState(new Set());

  const handleComplete = (id) => {
    setCompletingIds((prev) => new Set([...prev, id]));
    setTimeout(() => {
      if (onComplete) onComplete(id);
    }, 320);
  };

  return (
    <div style={{ ...DASH_CARD, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <div style={{
        padding: "0.875rem 1.25rem",
        borderBottom: "1px solid rgba(26,77,46,0.1)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <CheckCircle size={16} color="#3d6b1f" />
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 700 }}>
            Quick Wins
          </span>
        </div>
        <span style={{
          fontFamily: "'Manrope', sans-serif", fontSize: "0.72rem", fontWeight: 600,
          color: inboxTotal > 10 ? "#d97706" : "#6b7280",
          background: inboxTotal > 10 ? "rgba(217,119,6,0.1)" : "rgba(26,77,46,0.07)",
          border: `1px solid ${inboxTotal > 10 ? "rgba(217,119,6,0.3)" : "rgba(26,77,46,0.15)"}`,
          borderRadius: "2rem", padding: "0.2rem 0.6rem",
        }}>
          {inboxTotal} in inbox
        </span>
      </div>

      <div style={{ padding: "0.75rem", display: "flex", flexDirection: "column", gap: "0.375rem", flex: 1 }}>
        {tasks.length === 0 ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem", textAlign: "center" }}>
            <div>
              <div style={{ fontSize: "1.75rem", marginBottom: "0.4rem" }}>🎉</div>
              <p style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.82rem", margin: 0 }}>
                Inbox is empty!
              </p>
            </div>
          </div>
        ) : (
          tasks.map((task) => (
            <QuickWinRow
              key={task.id}
              task={task}
              completing={completingIds.has(task.id)}
              onComplete={() => handleComplete(task.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function QuickWinRow({ task, completing, onComplete }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "0.625rem",
      padding: "0.45rem 0.5rem",
      background: "rgba(255,255,255,0.5)",
      borderRadius: "0.5rem",
      border: "1px solid rgba(26,77,46,0.08)",
      opacity: completing ? 0 : 1,
      transform: completing ? "translateX(14px)" : "translateX(0)",
      maxHeight: completing ? "0px" : "48px",
      overflow: "hidden",
      transition: "opacity 0.3s ease, transform 0.3s ease, max-height 0.3s ease, margin 0.3s ease",
    }}>
      <button
        onClick={onComplete}
        data-testid={`quick-win-check-${task.id}`}
        style={{
          width: "18px", height: "18px", borderRadius: "50%", flexShrink: 0,
          border: "1.5px solid rgba(26,77,46,0.35)", background: "transparent",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          transition: "background 0.15s, border-color 0.15s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(61,107,31,0.12)";
          e.currentTarget.style.borderColor = "#3d6b1f";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.borderColor = "rgba(26,77,46,0.35)";
        }}
      />
      <span style={{
        fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.82rem",
        fontWeight: 500, flex: 1,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {task.title}
      </span>
      {task.is_overdue && (
        <span style={{
          fontFamily: "'Manrope', sans-serif", fontSize: "0.6rem", fontWeight: 700,
          background: "rgba(220,38,38,0.1)", color: "#dc2626",
          border: "1px solid rgba(220,38,38,0.25)", borderRadius: "0.3rem",
          padding: "0.05rem 0.35rem", flexShrink: 0, textTransform: "uppercase",
        }}>
          OVERDUE
        </span>
      )}
      {task.priority === "urgent" && !task.is_overdue && (
        <span style={{
          fontFamily: "'Manrope', sans-serif", fontSize: "0.6rem", fontWeight: 700,
          background: "rgba(220,38,38,0.1)", color: "#dc2626",
          border: "1px solid rgba(220,38,38,0.25)", borderRadius: "0.3rem",
          padding: "0.05rem 0.35rem", flexShrink: 0, textTransform: "uppercase",
        }}>
          URGENT
        </span>
      )}
    </div>
  );
}
